import { z } from "zod";

const prefix = "froppio:square:attempt:";
const lifetime = 15 * 60 * 1000;
const AttemptSchema = z.object({
  attemptId: z.uuid(),
  browserVerifier: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  businessId: z.string().min(1),
  locationId: z.string().min(1),
  userId: z.string().min(1),
  createdAt: z.number(),
  sameTab: z.boolean(),
});
export type SquareAttempt = z.infer<typeof AttemptSchema>;
export type SquareResult = "connected" | "cancelled" | "error";

export function removeSquareAttempt(attemptId: string) {
  try { sessionStorage.removeItem(`${prefix}${attemptId}`); } catch { /* Storage may be unavailable. */ }
}

export function readSquareAttempt(attemptId: string): SquareAttempt | null {
  try {
    const parsed = AttemptSchema.safeParse(JSON.parse(sessionStorage.getItem(`${prefix}${attemptId}`) ?? "null"));
    if (parsed.success && parsed.data.attemptId === attemptId &&
        parsed.data.createdAt <= Date.now() && Date.now() - parsed.data.createdAt < lifetime) {
      return parsed.data;
    }
  } catch { /* Malformed or unavailable storage is not browser proof. */ }
  removeSquareAttempt(attemptId);
  return null;
}

export function createSquareAttempt(businessId: string, locationId: string, userId: string): SquareAttempt {
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(prefix)) readSquareAttempt(key.slice(prefix.length));
  }
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const attempt: SquareAttempt = {
    attemptId: crypto.randomUUID(),
    browserVerifier: btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
    businessId, locationId, userId, createdAt: Date.now(), sameTab: false,
  };
  sessionStorage.setItem(`${prefix}${attempt.attemptId}`, JSON.stringify(attempt));
  return attempt;
}

export function markSquareSameTab(attempt: SquareAttempt) {
  sessionStorage.setItem(`${prefix}${attempt.attemptId}`, JSON.stringify({ ...attempt, sameTab: true }));
}

export function squareSettingsPath(attempt: SquareAttempt) {
  return `/b/${encodeURIComponent(attempt.businessId)}/l/${encodeURIComponent(attempt.locationId)}/square`;
}

export function allowedSquareAuthorizationUrl(value: string): string {
  const url = new URL(value);
  if (!["https://connect.squareup.com", "https://connect.squareupsandbox.com"].includes(url.origin) ||
      url.pathname !== "/oauth2/authorize" || url.username || url.password || url.hash) {
    throw new Error("Invalid authorization destination");
  }
  return url.href;
}

export function isSquareCompletionMessage(event: MessageEvent, popup: Window | null, attemptId: string) {
  return popup !== null && event.origin === window.location.origin && event.source === popup &&
    event.data?.type === "froppio:square:complete" && event.data?.attemptId === attemptId &&
    ["connected", "cancelled", "error"].includes(event.data?.result);
}
